import { useEffect, type RefObject } from 'react';

/**
 * Piège le focus à l'intérieur du `container` tant que `active` est vrai.
 * Restaure le focus précédent à la fermeture. Standard pour modales et
 * command palettes - indispensable pour l'accessibilité (WCAG 2.4.3).
 */
export function useFocusTrap(active: boolean, container: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!active || !container.current) return;

    const root = container.current;
    const previousFocus = document.activeElement as HTMLElement | null;

    function focusables(): HTMLElement[] {
      return Array.from(
        root.querySelectorAll<HTMLElement>(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
          ].join(', '),
        ),
      );
    }

    const initial = focusables()[0];
    initial?.focus();

    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && active === last) {
        first.focus();
        e.preventDefault();
      }
    }

    root.addEventListener('keydown', onKey);
    return () => {
      root.removeEventListener('keydown', onKey);
      previousFocus?.focus();
    };
  }, [active, container]);
}
